import { useCallback } from 'react';
import { useInstantSearch, useToggleRefinement } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { TextSwitch } from '@sorare/core/src/atoms/inputs/TextSwitch';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useInSeasonLocalStorage } from '@sorare/core/src/hooks/search/useInSeasonLocalStorage';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { FilterWidget, TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import {
  CURRENT_IN_SEASON_FOOTBALL,
  CURRENT_IN_SEASON_MLB,
  CURRENT_IN_SEASON_NBA,
} from '@sorare/core/src/lib/seasons';

const FILTER = TOGGLE_FILTERS.inSeasonFilter;

const Filter = () => {
  const {
    refine,
    value: { isRefined },
  } = useToggleRefinement({
    attribute: FILTER.attribute,
  });
  const track = useEvents();
  const { indexUiState } = useInstantSearch();
  const { sport } = useSportContext();
  const [, setInSeason] = useInSeasonLocalStorage();

  const handleChange = useCallback(
    (newValue: boolean) => {
      track('Use Market Filter', {
        filterName: FILTER.attribute,
        filterValue: newValue.toString(),
      });

      refine({ isRefined });
      setInSeason(newValue);
    },
    [track, refine, isRefined, setInSeason]
  );

  // we rather consider the refinement state from the UI
  // so that we don't rely on the Algolia answer to come back to reflect the slider state
  const optimisticRefined = !!indexUiState.toggle?.[FILTER.attribute];

  return (
    <div className="FilterWidget visible isActiveWidget">
      <TextSwitch
        checked={optimisticRefined}
        onChange={handleChange}
        onLabel={
          <BodyM bold>
            <FormattedMessage
              id="RefineInSeason.on"
              defaultMessage="{currentSeason} Season"
              values={{
                currentSeason: {
                  [Sport.FOOTBALL]: CURRENT_IN_SEASON_FOOTBALL,
                  [Sport.BASEBALL]: CURRENT_IN_SEASON_MLB,
                  [Sport.NBA]: CURRENT_IN_SEASON_NBA,
                }[sport ?? Sport.FOOTBALL],
              }}
            />
          </BodyM>
        }
        offLabel={
          <BodyM bold>
            <FormattedMessage id="RefineInSeason.off" defaultMessage="All" />
          </BodyM>
        }
      />
    </div>
  );
};

export const widget: FilterWidget = {
  key: FILTER.attribute,
  type: 'toggle',
  attribute: FILTER.attribute,
  Component: Filter,
};
